import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Star, Check, X, RefreshCw, Eye } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN   = () => sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
const H       = () => ({ Authorization: `Bearer ${TOKEN()}` });

function Stars({ n }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={12}
          style={{ fill: i <= n ? 'var(--admin-gold)' : 'none', color: i <= n ? 'var(--admin-gold)' : 'var(--admin-border)' }} />
      ))}
    </div>
  );
}

export default function AdminAvis() {
  const [reviews, setReviews]   = useState([]);
  const [tab,     setTab]       = useState('pending'); // pending | approved
  const [loading, setLoading]   = useState(true);
  const [stats,   setStats]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        fetch(`${BACKEND}/api/avis/admin/pending`, { headers: H() }).then(r => r.json()),
        fetch(`${BACKEND}/api/avis/stats`).then(r => r.json()),
      ]);
      setReviews(pRes.reviews || []);
      setStats(sRes);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function moderate(id, action) {
    try {
      await fetch(`${BACKEND}/api/avis/${id}/moderer?action=${action}`, {
        method: 'PUT', headers: H()
      });
      toast.success(action === 'approuver' ? 'Avis publié ✅' : 'Avis masqué');
      load();
    } catch { toast.error('Erreur'); }
  }

  return (
    <div data-testid="admin-avis">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Clients</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
              letterSpacing: '-.6px', color: 'var(--admin-text)' }}>Avis clients</h2>
            <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>
              Modération et publication des avis produits
            </p>
          </div>
          <button onClick={load}
            style={{ width: 34, height: 34, borderRadius: 10, border: '1px solid var(--admin-border)',
              background: 'var(--admin-surface)', cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: 'var(--admin-muted)' }}>
            <RefreshCw size={14}/>
          </button>
        </div>
      </div>

      {/* KPIs */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { label: 'Avis total',   value: stats.total,              color: 'var(--admin-blue)' },
            { label: 'Note moyenne', value: stats.moyenne ? `${stats.moyenne}/5 ⭐` : '—', color: 'var(--admin-gold)' },
            { label: 'En attente',   value: reviews.length,           color: reviews.length > 0 ? 'var(--admin-red)' : 'var(--admin-green)' },
          ].map((k, i) => (
            <div key={i} className="adm-card" style={{ padding: '12px 14px' }}>
              <p style={{ fontFamily: 'var(--admin-font-display)', fontSize: 20, fontWeight: 700, color: k.color, marginBottom: 3 }}>{k.value}</p>
              <p style={{ fontSize: 11.5, color: 'var(--admin-faint)' }}>{k.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--admin-border-soft)', marginBottom: 14 }}>
        {[{k:'pending',l:`En attente (${reviews.length})`},{k:'approved',l:'Publiés'}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ padding: '9px 14px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
              background: 'none', border: 'none', color: tab === t.k ? 'var(--admin-text)' : 'var(--admin-muted)',
              borderBottom: tab === t.k ? '2px solid var(--admin-teal)' : '2px solid transparent', marginBottom: -1 }}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Liste avis en attente */}
      {tab === 'pending' && (
        loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--admin-faint)' }}>Chargement...</div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--admin-faint)' }}>
            <Check size={36} style={{ margin: '0 auto 12px', opacity: .2 }}/>
            <p style={{ fontSize: 14 }}>Aucun avis en attente de modération</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {reviews.map(r => (
              <div key={r.id} className="adm-card" style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                      <Stars n={r.note || 5} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-text)' }}>
                        {r.client_nom || 'Anonyme'}
                      </span>
                      {r.product_nom && (
                        <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 7,
                          background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)' }}>
                          {r.product_nom}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--admin-faint)', marginLeft: 'auto' }}>
                        {formatDate(r.created_at)}
                      </span>
                    </div>
                    {r.commentaire && (
                      <p style={{ fontSize: 13, color: 'var(--admin-muted)', lineHeight: 1.5, marginBottom: 4 }}>
                        “{r.commentaire}”
                      </p>
                    )}
                    {/* Notes détaillées */}
                    {(r.note_livraison || r.note_proprete) && (
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
                        {r.note_livraison && <span style={{ fontSize: 11, color: 'var(--admin-faint)' }}>Livraison: <Stars n={r.note_livraison}/></span>}
                        {r.note_proprete  && <span style={{ fontSize: 11, color: 'var(--admin-faint)' }}>Propreté: <Stars n={r.note_proprete}/></span>}
                      </div>
                    )}
                  </div>
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => moderate(r.id, 'approuver')}
                      title="Approuver et publier"
                      style={{ width: 34, height: 34, borderRadius: 9, border: 'none', cursor: 'pointer',
                        background: 'var(--admin-green-dim)', color: 'var(--admin-green)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Check size={14}/>
                    </button>
                    <button onClick={() => moderate(r.id, 'masquer')}
                      title="Masquer"
                      style={{ width: 34, height: 34, borderRadius: 9, border: 'none', cursor: 'pointer',
                        background: 'var(--admin-red-dim)', color: 'var(--admin-red)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <X size={14}/>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Avis approuvés — lien vers l’API */}
      {tab === 'approved' && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Eye size={32} style={{ margin: '0 auto 12px', color: 'var(--admin-faint)', opacity: .3 }}/>
          <p style={{ fontSize: 14, color: 'var(--admin-faint)' }}>Les avis publiés sont visibles sur les fiches produit</p>
          <p style={{ fontSize: 12, color: 'var(--admin-faint)', marginTop: 6 }}>Note globale : {stats?.moyenne ? `${stats.moyenne}/5` : '—'} · {stats?.total || 0} avis</p>
        </div>
      )}
    </div>
  );
}
